/**
 * Song analysis on the main thread side (W2-08 timing; ARCHITECTURE.md §8-§9, DESIGN.md §5.2).
 *
 * One `TimingAnalysis` per DSP worker client (`timingAnalysisFor(services)`), shared by the Song & Timing panels,
 * the commands and the wizard Beat step. Per song (audio SHA-256):
 *  1. Peaks, onset envelope and tempo candidates come from the persist analysis cache (`AnalysisMap`) when present,
 *     so a song is analysed once per browser, not once per visit.
 *  2. Otherwise the audio worker computes them; results go to the cache. Every worker call is tried first as it is:
 *     the worker may already hold the song's PCM (installed by the wizard's Song step with `dsp.setPcm`, by the
 *     chart timeline, or earlier in this session). Only a call rejected with code 'noPcm' (never installed, or
 *     dropped as least recently used) installs the PCM and retries once.
 *  3. Where the PCM comes from (channels are COPIED before the transfer: the audio engine keeps its own):
 *     - the audio engine, once it has decoded this song (`AudioEngine.musicChannels`);
 *     - the song's file bytes registered with `provideFile` (the wizard's draft song), decoded here through an
 *       OfflineAudioContext into a mono downmix, when the engine does not hold the song and nobody is loading it
 *       into the engine (a chart is open and the engine plays its song), or when the engine only streams it;
 *     - otherwise the song waits ('waitingForAudio'); when the song is the open project's music and nothing loads
 *       it within a moment, the session loads it into the engine from the project's assets itself.
 *  4. Offset suggestions run in the worker (exact onset times and the low band from the PCM) and fall back to the
 *     cached onset envelope on this thread when the PCM is not available.
 * State is exposed as immutable snapshots per song (`get`, `subscribe`) for useSyncExternalStore.
 */

import { useCallback, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import { decodeAudio } from '../../audio/decode';
import { suggestOffset as suggestOffsetLocal, type BpmPointF64 } from '../../core/audio-dsp/offset';
import { ONSET_HOP } from '../../core/audio-dsp/onset';
import { MAX_BPM, MIN_BPM } from '../../core/audio-dsp/tempo';
import type { OnsetEnvelope, PeakLevel, TempoCandidate } from '../../core/audio-dsp/types';
import type { AssetId } from '../../core/model/types';
import type { Unsubscribe } from '../../core/util/types';

export type AnalysisStatus = 'idle' | 'waitingForAudio' | 'analysing' | 'ready' | 'error';

export interface SongAnalysis {
  readonly audio: AssetId;
  readonly status: AnalysisStatus;
  /** Worker task in progress ('peaks' | 'onsets' | 'tempo' | 'offset') and its progress 0..1. */
  readonly task: string | null;
  readonly progress: number | null;
  readonly peaks: readonly PeakLevel[] | null;
  readonly onsets: OnsetEnvelope | null;
  readonly sampleRate: number | null;
  readonly durationSec: number | null;
  readonly candidates: readonly TempoCandidate[] | null;
  readonly detecting: boolean;
  readonly error: string | null;
}

export interface OffsetResult {
  readonly offsetMs: number;
  readonly confidence: number;
}

export interface TimingAnalysis {
  get(audio: AssetId): SongAnalysis;
  subscribe(cb: () => void): Unsubscribe;
  /** Starts loading the peaks and the onset envelope (no-op when already started). */
  ensure(audio: AssetId): void;
  /** Tempo candidates (cache, else the worker). `force` re-runs the detection. */
  detectTempo(audio: AssetId, opts?: { readonly force?: boolean }): Promise<readonly TempoCandidate[]>;
  /** Offset suggestion in DECODED music time (ms), or null when the song cannot be analysed. */
  suggestOffset(audio: AssetId, bpm: readonly BpmPointF64[], currentOffsetMs: number): Promise<OffsetResult | null>;
  /** Restarts a song whose analysis failed. */
  retry(audio: AssetId): void;
  /**
   * Loads a song into the audio engine now: the open project's song from its assets (the "Load the song" button),
   * or from `bytes` (the wizard's draft song). Analyses waiting for it continue once it is decoded.
   */
  loadSong(audio: AssetId, bytes?: Uint8Array): Promise<void>;
  /**
   * Registers the file of a song that may never reach the audio engine (the wizard's draft song while a chart is
   * open): when the worker needs its PCM and the engine does not hold it, the session decodes these bytes itself.
   * Returns the disposer (the session lets go of the bytes).
   */
  provideFile(audio: AssetId, bytes: Uint8Array): Unsubscribe;
  /**
   * Lets go of every analysed song (peaks, onset envelopes, tempo candidates, registered files) — called when the
   * open project closes (UX-023 / CR-W3-01-5: the analysis peaks must not outlive the project). Subscribers are
   * kept; `ensure` on a new project's song starts fresh.
   */
  release(): void;
}

/** Wait before the session loads the project's song into the engine itself. */
export const AUTOLOAD_DELAY_MS = 1500;

type Deps = Pick<AppServices, 'dsp' | 'persist' | 'audio'> & { readonly store: AppServices['store'] };

const sessions = new WeakMap<object, { session: TimingAnalysis; services: { current: Deps } }>();

/** The analysis session of an app (keyed by its DSP worker client). */
export function timingAnalysisFor(services: Deps): TimingAnalysis {
  const hit = sessions.get(services.dsp);
  if (hit !== undefined) {
    hit.services.current = services;
    return hit.session;
  }
  const ref = { current: services };
  const session = createTimingAnalysis(ref);
  sessions.set(services.dsp, { session, services: ref });
  return session;
}

function empty(audio: AssetId): SongAnalysis {
  return {
    audio,
    status: 'idle',
    task: null,
    progress: null,
    peaks: null,
    onsets: null,
    sampleRate: null,
    durationSec: null,
    candidates: null,
    detecting: false,
    error: null,
  };
}

/** `SongAnalysis.error` of a song the engine plays from the file without decoding it (no PCM to analyse). */
export const NOT_DECODED = 'notDecoded';
/** `SongAnalysis.error` when the browser offers no decoder for the analysis (no OfflineAudioContext). */
export const NO_DECODER = 'noDecoder';
/** `SongAnalysis.error` when the browser could not decode the song's file for the analysis. */
export const DECODE_FAILED = 'decodeFailed';

export type AnalysisErrorCode = typeof NOT_DECODED | typeof NO_DECODER | typeof DECODE_FAILED;

/** A failure with a known cause (its message is a code the UI translates). */
export class AnalysisError extends Error {
  readonly code: AnalysisErrorCode;
  constructor(code: AnalysisErrorCode) {
    super(code);
    this.name = 'AnalysisError';
    this.code = code;
  }
}

/** Sample rate the session decodes a song file at when the audio engine has no context yet. */
export const DEFAULT_DECODE_RATE = 44100;

interface Pcm {
  readonly channels: Float32Array[];
  readonly sampleRate: number;
}

/**
 * Decodes a song file into its mono downmix (one channel: the worker keeps a mono downmix anyway, and one channel
 * halves the transfer). `sampleRate`: the rate decodeAudioData resamples to.
 */
export async function decodeSongFile(bytes: Uint8Array, sampleRate: number): Promise<Pcm> {
  const Ctor = (globalThis as { OfflineAudioContext?: typeof OfflineAudioContext }).OfflineAudioContext;
  if (Ctor === undefined) throw new AnalysisError(NO_DECODER);
  let buffer: AudioBuffer;
  try {
    buffer = await decodeAudio(new Ctor(1, 1, sampleRate), bytes);
  } catch {
    throw new AnalysisError(DECODE_FAILED);
  }
  const n = buffer.numberOfChannels;
  if (n === 0 || buffer.length === 0) throw new AnalysisError(DECODE_FAILED);
  const mono = new Float32Array(buffer.length);
  if (n === 1) mono.set(buffer.getChannelData(0));
  else {
    for (let c = 0; c < n; c++) {
      const data = buffer.getChannelData(c);
      for (let i = 0; i < mono.length; i++) mono[i] = (mono[i] ?? 0) + (data[i] ?? 0) / n;
    }
  }
  return { channels: [mono], sampleRate: buffer.sampleRate };
}

function errorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

function errorCode(err: unknown): string | undefined {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : undefined;
}

/** Sample rate implied by an onset envelope (hop 512). */
export function sampleRateOf(onsets: OnsetEnvelope): number {
  return Math.round(ONSET_HOP / onsets.hopSec);
}

function createTimingAnalysis(ref: { current: Deps }): TimingAnalysis {
  const states = new Map<AssetId, SongAnalysis>();
  const listeners = new Set<() => void>();
  const started = new Set<AssetId>();
  const installing = new Map<AssetId, Promise<void>>();
  /** Songs being loaded into the audio engine by `loadSong` (their PCM comes from the engine). */
  const loading = new Set<AssetId>();
  /** Song files registered with `provideFile` (latest registration last). */
  const files = new Map<AssetId, Uint8Array[]>();
  /** Installs waiting for a source of PCM: called again whenever one may have appeared. */
  const waiters = new Set<() => void>();
  let progressFor: AssetId | null = null;
  let progressOff: Unsubscribe | null = null;

  const deps = (): Deps => ref.current;

  const notify = (): void => {
    for (const cb of [...listeners]) cb();
  };

  const wake = (): void => {
    for (const w of [...waiters]) w();
  };

  const get = (audio: AssetId): SongAnalysis => {
    let s = states.get(audio);
    if (s === undefined) {
      s = empty(audio);
      states.set(audio, s);
    }
    return s;
  };

  const patch = (audio: AssetId, p: Partial<SongAnalysis>): void => {
    states.set(audio, { ...get(audio), ...p });
    notify();
  };

  const listenProgress = (audio: AssetId): void => {
    progressFor = audio;
    if (progressOff !== null) return;
    progressOff = deps().dsp.on('progress', ({ task, p }) => {
      if (progressFor !== null) patch(progressFor, { task, progress: p });
    });
  };

  /** The decoded channels of `audio` when the engine has decoded it. */
  const pcmNow = (audio: AssetId): Pcm | null => {
    const { audio: engine } = deps();
    const music = engine.music;
    if (music?.asset !== audio) return null;
    const channels = engine.musicChannels();
    if (channels === null || channels.length === 0 || (channels[0]?.length ?? 0) === 0) return null;
    return { channels: channels.map((c) => c.slice()), sampleRate: music.sampleRate };
  };

  /** The engine has this song but plays it from the file (no decoded PCM to analyse). */
  const notDecodable = (audio: AssetId): boolean => {
    const { audio: engine } = deps();
    return engine.music?.asset === audio && engine.music.streaming && pcmNow(audio) === null;
  };

  const fileOf = (audio: AssetId): Uint8Array | null => {
    const list = files.get(audio);
    return list?.[list.length - 1] ?? null;
  };

  /** The rate a song file is decoded at: the engine's (so both agree on sample positions), else 44.1 kHz. */
  const decodeRate = (): number => {
    const engine = deps().audio;
    const rate = engine.context?.sampleRate ?? engine.music?.sampleRate ?? DEFAULT_DECODE_RATE;
    return Number.isFinite(rate) && rate >= 8000 ? rate : DEFAULT_DECODE_RATE;
  };

  const loadSong = async (audio: AssetId, bytes?: Uint8Array): Promise<void> => {
    if (loading.has(audio)) return;
    const d = deps();
    if (d.audio.music?.asset === audio) return;
    loading.add(audio);
    try {
      const data = bytes ?? (await d.persist.getAsset(audio));
      const buffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
      await d.audio.loadMusic(audio, buffer);
    } finally {
      loading.delete(audio);
      // Waiting analyses continue: from the engine's PCM, or from a registered file when the engine failed or
      // only streams the song.
      wake();
    }
  };

  /** Resolves once the worker holds the song's PCM (waits for a source: engine, file or autoload). */
  const ensurePcm = (audio: AssetId): Promise<void> => {
    const pending = installing.get(audio);
    if (pending !== undefined) return pending;
    const p = new Promise<void>((resolve, reject) => {
      let off: Unsubscribe | null = null;
      let timer: ReturnType<typeof setTimeout> | null = null;
      let done = false;
      /** Status before the wait ('waitingForAudio' is only shown while waiting). */
      let before: AnalysisStatus | null = null;
      const finish = (): void => {
        done = true;
        off?.();
        off = null;
        waiters.delete(attempt);
        if (timer !== null) clearTimeout(timer);
        timer = null;
        if (before !== null && get(audio).status === 'waitingForAudio') {
          patch(audio, { status: before });
        }
        before = null;
      };
      const install = (pcm: Pcm): void => {
        deps()
          .dsp.call(
            'setPcm',
            { audio, channels: pcm.channels, sampleRate: pcm.sampleRate },
            pcm.channels.map((c) => c.buffer),
          )
          .then(resolve, (err: unknown) => {
            reject(err instanceof Error ? err : new Error(errorText(err)));
          });
      };
      /** Installs from the first available source; true once the wait is over (installing or failed). */
      function attempt(): boolean {
        if (done) return true;
        const pcm = pcmNow(audio);
        if (pcm !== null) {
          finish();
          install(pcm);
          return true;
        }
        // The engine is loading this song: its PCM is on the way.
        if (loading.has(audio)) return false;
        const file = fileOf(audio);
        if (file !== null) {
          finish();
          if (get(audio).status === 'analysing') patch(audio, { task: 'decode', progress: null });
          decodeSongFile(file, decodeRate()).then(
            (decoded) => {
              if (get(audio).task === 'decode') patch(audio, { task: null });
              install(decoded);
            },
            (err: unknown) => {
              reject(err instanceof Error ? err : new Error(errorText(err)));
            },
          );
          return true;
        }
        if (notDecodable(audio)) {
          finish();
          reject(new AnalysisError(NOT_DECODED));
          return true;
        }
        return false;
      }
      if (attempt()) return;
      const status = get(audio).status;
      if (status !== 'ready' && status !== 'waitingForAudio') {
        before = status;
        patch(audio, { status: 'waitingForAudio' });
      }
      waiters.add(attempt);
      off = deps().audio.transport.onChange(() => {
        attempt();
      });
      timer = setTimeout(() => {
        timer = null;
        if (done) return;
        const d = deps();
        if (d.store?.doc.meta.music === audio && !d.audio.transport.state.playing && !loading.has(audio)) {
          loadSong(audio).catch((err: unknown) => {
            if (done) return;
            finish();
            reject(err instanceof Error ? err : new Error(errorText(err)));
          });
        }
      }, AUTOLOAD_DELAY_MS);
    }).finally(() => {
      installing.delete(audio);
    });
    installing.set(audio, p);
    return p;
  };

  /**
   * A worker call for `audio`: tried as it is (the worker may hold the PCM already); when it rejects with code
   * 'noPcm', the PCM is installed and the call retried once.
   */
  const withPcm = async <T>(audio: AssetId, run: () => Promise<T>): Promise<T> => {
    listenProgress(audio);
    try {
      return await run();
    } catch (err) {
      if (errorCode(err) !== 'noPcm') throw err;
    }
    await ensurePcm(audio);
    listenProgress(audio);
    return run();
  };

  const load = async (audio: AssetId): Promise<void> => {
    const d = deps();
    patch(audio, { status: 'analysing', task: null, progress: null, error: null });
    try {
      const [cachedPeaks, cachedOnsets, cachedTempo] = await Promise.all([
        d.persist.analysisGet(audio, 'peaks').catch(() => undefined),
        d.persist.analysisGet(audio, 'onsets').catch(() => undefined),
        d.persist.analysisGet(audio, 'tempo').catch(() => undefined),
      ]);
      if (cachedTempo !== undefined && get(audio).candidates === null) patch(audio, { candidates: cachedTempo });
      let peaks = cachedPeaks ?? null;
      let onsets = cachedOnsets ?? null;
      if (peaks === null || onsets === null) {
        if (peaks === null) {
          const p = await withPcm(audio, () => deps().dsp.call('peaks', { audio }));
          peaks = p;
          void deps()
            .persist.analysisSet(audio, 'peaks', p)
            .catch(() => undefined);
        }
        if (onsets === null) {
          const o = await withPcm(audio, () => deps().dsp.call('onsets', { audio }));
          onsets = o;
          void deps()
            .persist.analysisSet(audio, 'onsets', o)
            .catch(() => undefined);
        }
      }
      const sampleRate = sampleRateOf(onsets);
      patch(audio, {
        status: 'ready',
        task: null,
        progress: null,
        peaks,
        onsets,
        sampleRate,
        durationSec: onsets.env.length * onsets.hopSec,
      });
    } catch (err) {
      started.delete(audio);
      patch(audio, { status: 'error', task: null, progress: null, error: errorText(err) });
    }
  };

  const ensure = (audio: AssetId): void => {
    if (started.has(audio)) return;
    started.add(audio);
    void load(audio);
  };

  const detectTempo = async (
    audio: AssetId,
    opts: { readonly force?: boolean } = {},
  ): Promise<readonly TempoCandidate[]> => {
    const cur = get(audio);
    if (opts.force !== true && cur.candidates !== null) return cur.candidates;
    if (opts.force !== true) {
      const cached = await deps()
        .persist.analysisGet(audio, 'tempo')
        .catch(() => undefined);
      if (cached !== undefined) {
        patch(audio, { candidates: cached });
        return cached;
      }
    }
    patch(audio, { detecting: true, error: null });
    try {
      const c = await withPcm(audio, () =>
        deps().dsp.call('tempoCandidates', { audio, minBpm: MIN_BPM, maxBpm: MAX_BPM }),
      );
      void deps()
        .persist.analysisSet(audio, 'tempo', c)
        .catch(() => undefined);
      patch(audio, { candidates: c, detecting: false, task: null, progress: null });
      return c;
    } catch (err) {
      patch(audio, { detecting: false, task: null, progress: null, error: errorText(err) });
      throw err;
    }
  };

  const suggestOffset = async (
    audio: AssetId,
    bpm: readonly BpmPointF64[],
    currentOffsetMs: number,
  ): Promise<OffsetResult | null> => {
    const run = (): Promise<OffsetResult> => deps().dsp.call('suggestOffset', { audio, bpm, currentOffsetMs });
    const local = (): OffsetResult | null => {
      const onsets = get(audio).onsets;
      return onsets === null ? null : suggestOffsetLocal(onsets, bpm, currentOffsetMs);
    };
    listenProgress(audio);
    try {
      return await run();
    } catch (err) {
      if (errorCode(err) !== 'noPcm') return local();
    }
    // The worker lacks the PCM: install it when a source is at hand, else answer from the cached envelope (null
    // without one) right away instead of waiting for a song nobody is loading.
    const sourceNow = pcmNow(audio) !== null || fileOf(audio) !== null || loading.has(audio);
    if (!sourceNow) return local();
    try {
      await ensurePcm(audio);
      listenProgress(audio);
      return await run();
    } catch {
      return local();
    }
  };

  return {
    get,
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    ensure,
    detectTempo,
    suggestOffset,
    retry(audio) {
      started.delete(audio);
      patch(audio, { status: 'idle', error: null });
      ensure(audio);
    },
    loadSong,
    provideFile(audio, bytes) {
      const list = files.get(audio) ?? [];
      list.push(bytes);
      files.set(audio, list);
      wake();
      return () => {
        const cur = files.get(audio);
        if (cur === undefined) return;
        const i = cur.lastIndexOf(bytes);
        if (i >= 0) cur.splice(i, 1);
        if (cur.length === 0) files.delete(audio);
      };
    },
    release() {
      states.clear();
      started.clear();
      installing.clear();
      loading.clear();
      files.clear();
      waiters.clear();
      progressFor = null;
      if (progressOff !== null) {
        progressOff();
        progressOff = null;
      }
      notify();
    },
  };
}

/** The analysis snapshot of a song (null audio: an idle placeholder that never changes). */
export function useSongAnalysis(services: Deps, audio: AssetId | null): SongAnalysis | null {
  const analysis = timingAnalysisFor(services);
  const subscribe = useCallback((cb: () => void) => analysis.subscribe(cb), [analysis]);
  const get = useCallback(() => (audio === null ? null : analysis.get(audio)), [analysis, audio]);
  return useSyncExternalStore(subscribe, get, get);
}
